/* Gloramous — cart drawer, quick-add, search, WhatsApp order,
   recently viewed, image zoom.
   Separate file from theme.js so the storefront logic stays readable.

   Everything here is progressive enhancement:
   - with JS off, the product form posts normally and lands on /cart
   - quick-add buttons are injected by script, so they never appear broken
   - filter state is written to the URL so a filtered catalogue can be shared

   PREVIEW MODE: the static preview has no Shopify behind it, so when
   window.GLORAMOUS_PREVIEW is set the cart calls are faked from the DOM. The
   real theme never sets it. */
(function () {
  'use strict';

  var PREVIEW = !!window.GLORAMOUS_PREVIEW;
  // /ar/ on Arabic pages: cart calls in the page's language return translated titles
  var ROOT = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  // Set on <body> by layout/theme.liquid. Read once so the drawer, the cart
  // page and the product page all compose the same message.
  var WA = document.body.getAttribute('data-wa') || '';
  var WA_INTRO = document.body.getAttribute('data-wa-intro') || '';
  // flat delivery fee in cents, from settings.delivery_fee_cents via <body>
  var FEE = parseInt(document.body.getAttribute('data-delivery-fee') || '0', 10) || 0;
  var money = function (cents) {
    return '$' + (cents / 100).toFixed(2);
  };
  var CALM = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  /* theme.js publishes the roll; fall back to a plain write if it is absent */
  function roll(el, html) {
    if (!el) return;
    if (window.GloramousRoll) window.GloramousRoll(el, html);
    else el.innerHTML = html;
  }

  /* ═══ drawer shell, built once and reused ═══════════════════════════ */
  var scrim, drawer, body, sub, nudgeSlot, lastFocus;

  function build() {
    if (drawer) return;
    scrim = document.createElement('div');
    scrim.className = 'drawer-scrim';
    scrim.hidden = false;

    drawer = document.createElement('aside');
    drawer.className = 'drawer';
    drawer.setAttribute('role', 'dialog');
    drawer.setAttribute('aria-modal', 'true');
    drawer.setAttribute('aria-label', 'Cart');
    drawer.innerHTML =
      '<div class="drawer-head"><h2 data-drawer-title>Cart</h2>' +
      '<button class="drawer-close" type="button">Close</button></div>' +
      '<div class="drawer-body"></div>' +
      '<div class="drawer-foot">' +
      '<div class="drawer-sub"><span>Subtotal</span><span data-sub></span></div>' +
      /* The gift note and WhatsApp ordering were taken out of the drawer by the
         owner on 2026-09-24, and the delivery line went with them. It is back
         at the user's request (round 2, #3): the $4 is the first thing a cash
         on delivery buyer wants to know, and finding it only at the end reads
         as a surprise. Delete this one line to undo. */
      (FEE > 0 ? '<div class="drawer-sub drawer-ship"><span>Delivery</span><span>' + money(FEE) + '</span></div>' : '') +
      '<div data-nudge-slot></div>' +
      /* Round 2 #25: this was a link to /cart, so ordering from the drawer meant
         a page load and a second button. It now opens the order form from here.
         The handler is in snippets/easysell-overrides.liquid, with the rest of
         the EasySell wiring. */
      '<button class="btn btn--wide" type="button" data-drawer-order>Order now</button></div>';

    document.body.appendChild(scrim);
    document.body.appendChild(drawer);
    body = drawer.querySelector('.drawer-body');
    sub = drawer.querySelector('[data-sub]');
    nudgeSlot = drawer.querySelector('[data-nudge-slot]');

    scrim.addEventListener('click', close);
    drawer.querySelector('.drawer-close').addEventListener('click', close);

    /* the routine suggestion's Add button (it sits in the footer) */
    drawer.addEventListener('click', function (e) {
      var nb = e.target.closest ? e.target.closest('[data-nudge-add]') : null;
      if (!nb || !nudgeData) return;
      var n = nudgeData[nb.getAttribute('data-nudge-add')];
      if (!n) return;
      nb.disabled = true;
      addToCart(n.id, 1, { title: n.title, variant: n.variant, price: n.price, image: n.image })
        .then(function (cart) { setCount(cart.item_count); render(cart); })
        .catch(function (err) { nb.disabled = false; toast(err.message || 'Could not add that'); });
    });

    /* ── quantity and remove, inside the drawer ─────────────────────────
       The drawer used to list the lines and nothing else: changing a
       quantity meant leaving for the cart page. Now each line has - / + and
       Remove. A removal folds the line away and the toast offers Undo, which
       re-adds exactly what was there. */
    body.addEventListener('click', function (e) {
      var t = e.target.closest ? e.target.closest('[data-dq],[data-drm]') : null;
      if (!t || !lastCart || body.getAttribute('aria-busy') === 'true') return;
      var line = t.closest('.dline');
      var idx = parseInt(line.getAttribute('data-line'), 10);
      var item = lastCart.items[idx];
      if (!item) return;
      var removing = t.hasAttribute('data-drm');
      var qty = removing ? 0 : item.quantity + parseInt(t.getAttribute('data-dq'), 10);
      var which = removing ? null : t.getAttribute('data-dq');
      var undo = {
        id: item.id, quantity: item.quantity,
        meta: { title: item.product_title,
                variant: item.variant_title === 'Default Title' ? '' : item.variant_title,
                price: item.price || Math.round(item.line_price / Math.max(1, item.quantity)),
                image: item.image }
      };
      body.setAttribute('aria-busy', 'true');
      line.classList.add('is-busy');

      function go() {
        changeLine(idx, qty).then(function (cart) {
          cartChanged = true;
          render(cart);
          setCount(cart.item_count);
          if (qty <= 0) {
            toast(undo.meta.title + ' removed', 'Undo', function () {
              addToCart(undo.id, undo.quantity, undo.meta).then(function (c) {
                render(c);
                setCount(c.item_count);
              });
            });
            var first = body.querySelector('[data-dq]');
            (first || drawer.querySelector('.drawer-close')).focus({ preventScroll: true });
          } else {
            var same = body.querySelector('.dline[data-line="' + idx + '"] [data-dq="' + which + '"]');
            if (same) same.focus({ preventScroll: true });
          }
        }).catch(function (err) {
          line.classList.remove('is-busy', 'is-leaving');
          toast(err.message || 'Could not update the cart');
        }).then(function () {
          body.removeAttribute('aria-busy');
        });
      }
      if (qty <= 0 && !CALM) {
        line.style.height = line.offsetHeight + 'px';
        void line.offsetHeight;
        line.classList.add('is-leaving');
        setTimeout(go, 240);
      } else {
        go();
      }
    });

    /* ── swipe the drawer away ──────────────────────────────────────────
       On a phone this is a full-width takeover whose only exit is a Close
       button in the corner -- the one place a thumb holding the phone cannot
       reach. It takes a rightward swipe on its header instead.

       The dismissal rule is the projected release position, not distance or
       velocity separately: two independent thresholds means the union of
       them, and the union fires on gestures that were neither far nor fast.
       Measured on the filter sheet, where a deliberate 55px drag dismissed
       because it happened to run at 760 px/s. */
    (function () {
      var handle = drawer.querySelector('.drawer-head');
      if (!handle || !window.PointerEvent) return;
      var id = null, x0 = 0, dx = 0, trail = [];

      handle.addEventListener('pointerdown', function (e) {
        if (!drawer.classList.contains('is-open')) return;
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        if (e.target.closest('button,a')) return;     // Close is still a button
        id = e.pointerId; x0 = e.clientX; dx = 0;
        trail = [{ x: e.clientX, t: performance.now() }];
        try { handle.setPointerCapture(id); } catch (_) {}
        drawer.classList.add('is-dragging');
      });

      handle.addEventListener('pointermove', function (e) {
        if (id === null || e.pointerId !== id) return;
        /* measured toward the drawer's own outer edge: right in English, left
           in Arabic (RTL), where the drawer sits on the left */
        var sgn = document.documentElement.dir === 'rtl' ? -1 : 1;
        dx = (e.clientX - x0) * sgn;
        /* inward is resisted rather than blocked: a panel that simply stops
           moving reads as broken, one that resists reads as attached */
        var v = dx < 0 ? dx * 0.18 : dx;
        drawer.style.transform = 'translate3d(' + (v * sgn).toFixed(1) + 'px,0,0)';
        trail.push({ x: e.clientX, t: performance.now() });
        if (trail.length > 10) trail.shift();
      });

      function release(e) {
        if (id === null || (e && e.pointerId !== id)) return;
        var pid = id; id = null;
        try { handle.releasePointerCapture(pid); } catch (_) {}
        drawer.classList.remove('is-dragging');
        if (!trail.length) return;
        /* the trailing 90ms, so a slow drag ending in a flick still counts */
        var end = trail[trail.length - 1], i = trail.length - 1;
        while (i > 0 && end.t - trail[i - 1].t < 90) i--;
        var dt = end.t - trail[i].t;
        var vel = dt > 0 ? (end.x - trail[i].x) / dt * 1000 : 0;
        if (document.documentElement.dir === 'rtl') vel = -vel;
        var w = drawer.getBoundingClientRect().width || 1;
        drawer.style.transform = '';
        if (dx + vel * 0.15 > w * 0.28) close();
      }
      handle.addEventListener('pointerup', release);
      handle.addEventListener('pointercancel', release);
      handle.addEventListener('lostpointercapture', release);
    })();
    drawer.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') close();
      if (e.key !== 'Tab') return;
      // keep focus inside the dialog while it is open
      var f = drawer.querySelectorAll('a[href],button:not([disabled]),input');
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { last.focus(); e.preventDefault(); }
      else if (!e.shiftKey && document.activeElement === last) { first.focus(); e.preventDefault(); }
    });
  }

  var pointerRecent = false, lastWasPointer = false;
  document.addEventListener('pointerdown', function () { pointerRecent = true; }, true);
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') pointerRecent = false;
    /* a keyboard user has arrived: every suppressed ring comes back, without
       relying on a blur that may never fire */
    if (e.key === 'Tab') {
      document.querySelectorAll('.no-ring').forEach(function (el) { el.classList.remove('no-ring'); });
    }
  }, true);

  function open(title) {
    // a product picture is on its way to the cart icon: open once it lands
    if (flying) {
      var f = flying;
      flying = null;
      f.then(function () { open(title); });
      return;
    }
    build();
    var h = drawer.querySelector('[data-drawer-title]');
    if (h) h.textContent = title || 'Cart';
    lastFocus = document.activeElement;
    lastWasPointer = pointerRecent;
    if (lastFocus && lastFocus.classList) lastFocus.classList.remove('no-ring');
    scrim.classList.add('is-open');
    drawer.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    body.classList.remove('is-fresh');
    void body.offsetWidth;
    body.classList.add('is-fresh');
    clearTimeout(body._fresh);
    body._fresh = setTimeout(function () { body.classList.remove('is-fresh'); }, 900);
    drawer.querySelector('.drawer-close').focus();
  }

  function close() {
    if (!drawer) return;
    /* clear any drag transform: an inline transform outranks the class that
       slides the panel, so a swiped-away drawer would reopen offset by
       however far the finger had taken it */
    drawer.style.transform = '';
    scrim.classList.remove('is-open');
    drawer.classList.remove('is-open');
    document.body.style.overflow = '';
    /* on the cart page the rows are server-rendered, so a change made in the
       drawer would leave them showing the old cart */
    if (cartChanged && !PREVIEW && document.querySelector('[data-cart-form]')) {
      location.reload();
      return;
    }
    if (lastFocus && lastFocus.focus) {
      /* Focus goes back where it came from, but a drawer opened with the mouse
         must not come back with a keyboard focus ring: Chrome drew the 2px
         gold outline around the gold-bordered Cart pill and it read as a
         double border. Keyboard users still get the ring. */
      if (lastWasPointer) {
        var lf = lastFocus;
        lf.classList.add('no-ring');
        lf.addEventListener('blur', function () { lf.classList.remove('no-ring'); }, { once: true });
      }
      lastFocus.focus({ preventScroll: true });
    }
  }

  var lastCart = null, cartChanged = false;

  function render(cart) {
    build();
    lastCart = cart;
    if (drawer) drawer.classList.toggle('is-empty', !(cart && cart.items && cart.items.length));
    if (!cart || !cart.items || !cart.items.length) {
      /* an empty drawer is a dead end unless it says where to go */
      body.innerHTML = '<div class="drawer-empty"><p>Your cart is empty.</p>' +
        '<a class="btn btn--small" href="' + (PREVIEW ? 'catalogue.html' : ((window.GLORAMOUS_URLS && window.GLORAMOUS_URLS.catalogue) || '/collections/all')) +
        '">Browse the catalogue</a></div>';
      roll(sub, money(0));
      nudgeSlot.innerHTML = '';
      waApply(cart);
      return;
    }
    body.innerHTML = cart.items.map(function (i, idx) {
      var img = i.image
        ? '<img src="' + esc(i.image) + '" alt="">'
        : '<span class="ph"></span>';
      // the volume is a separate span, not a dot-joined string
      var variant = (i.variant_title && i.variant_title !== 'Default Title')
        ? '<span class="dline-vol">' + esc(i.variant_title) + '</span>' : '';
      var unit = i.quantity ? Math.round(i.line_price / i.quantity) : i.line_price;
      var name = esc(i.product_title);
      return '<div class="dline" data-line="' + idx + '" style="--i:' + idx + '">' + img +
        '<div class="dline-main"><b>' + name + '</b>' +
        '<span>' + money(unit) + ' each</span>' + variant +
        '<div class="dline-qty">' +
          '<div class="dstep">' +
            '<button type="button" data-dq="-1" aria-label="One fewer ' + name + '"' +
              (i.quantity <= 1 ? ' data-last' : '') + '>&minus;</button>' +
            '<output aria-live="polite">' + i.quantity + '</output>' +
            '<button type="button" data-dq="1" aria-label="One more ' + name + '">+</button>' +
          '</div>' +
          '<button type="button" class="dline-rm" data-drm aria-label="Remove ' + name + '">Remove</button>' +
        '</div></div>' +
        '<div class="dprice">' + money(i.line_price) + '</div></div>';
    }).join('');
    roll(sub, money(cart.total_price));
    nudgeSlot.innerHTML = nudgeFor(cart);
    waApply(cart);
  }

  /* ── complete your routine ─────────────────────────────────────────────
     One suggestion at most, and only when a routine is visibly missing a step:
     sunscreen after an acid or a brightening product, sunscreen for any face
     treatment, a cleanser when there is a treatment and no cleanser. Judged
     from the product names in the cart; the two products it can offer come
     from the page ([data-nudge]), so the theme decides what they are. */
  var nudgeData = null;
  try {
    var nd = document.querySelector('[data-nudge]');
    nudgeData = nd ? JSON.parse(nd.textContent) : null;
  } catch (e) { nudgeData = null; }

  function nudgeFor(cart) {
    if (!nudgeData || !cart || !cart.items || !cart.items.length) return '';
    var names = cart.items.map(function (i) { return String(i.product_title || ''); });
    /* Judged by product type as well as name (2026-09-24): by name alone the
       underarm Whitening Spray read as a brightening face product, Hand and
       Massage Cream as face care, and the Intimate Wash as a face cleanser. */
    var FACE = /^(Face Serum|Face Cream|Face Mask|Eye Care|Acid & Peel|Exfoliator & Scrub|Sun Care|Cleanser & Toner)$/;
    var face = cart.items.filter(function (i) {
      var t = String(i.product_type || ''), n = String(i.product_title || '');
      return FACE.test(t) && !/coffee scrub|tanning|shimmer|after sun|body/i.test(n);
    });
    var anyFace = function (re) { return face.some(function (i) { return re.test(String(i.product_title || '')); }); };
    var hasSpf = anyFace(/sunscreen|spf|sun protection/i);
    var hasCleanser = face.some(function (i) { return i.product_type === 'Cleanser & Toner' && /wash|cleans|micellar|remover/i.test(i.product_title); });
    var strongOrBright = face.some(function (i) {
      var n = String(i.product_title || '');
      return (i.product_type === 'Acid & Peel' || /acid|peel|retinol|kojic|whitening|vitamin c|acne lotion/i.test(n)) && !/hyaluronic/i.test(n);
    });
    var faceCare = face.some(function (i) { return i.product_type !== 'Cleanser & Toner' && i.product_type !== 'Sun Care'; });
    var key = null, why = '';
    if (!hasSpf && strongOrBright && nudgeData.spf) {
      key = 'spf'; why = 'Acids and brightening products make skin more sensitive to the sun. Wear sunscreen every morning.';
    } else if (!hasSpf && faceCare && nudgeData.spf) {
      key = 'spf'; why = 'Sunscreen is the last step of every morning routine.';
    } else if (!hasCleanser && faceCare && nudgeData.cleanser) {
      key = 'cleanser'; why = 'Every routine starts with a gentle cleanser.';
    }
    if (!key) return '';
    var n = nudgeData[key];
    if (names.indexOf(n.title) > -1) return '';
    return '<div class="nudge"><p class="nudge-h">Complete your routine</p><p class="nudge-why">' + esc(why) + '</p>' +
      '<div class="nudge-item">' + (n.image ? '<img src="' + esc(n.image) + '" alt="" width="56" height="56">' : '') +
      '<span class="nudge-text"><b>' + esc(n.title) + '</b><span>' + money(n.price) + '</span></span>' +
      '<button type="button" class="btn btn--small" data-nudge-add="' + key + '">Add</button></div></div>';
  }

  /* ── gift note ─────────────────────────────────────────────────────────
     One note for the order, whether it is written in the drawer or on the
     cart page. Kept in the browser at once, and on Shopify saved to the cart
     as attributes ("Gift message", "Gift: hide prices") so it travels with the
     order and can be printed on the packing slip. It also goes into the
     WhatsApp order message. */
  var GIFT_KEY = 'gloramous.gift';
  function giftGet() {
    try { return JSON.parse(localStorage.getItem(GIFT_KEY)) || { msg: '', hide: false }; }
    catch (e) { return { msg: '', hide: false }; }
  }
  /* delivery instructions are saved as they are typed: orders go through the
     cash-on-delivery app's popup, which never submits the cart form, so the
     note used to be lost */
  var noteBox = document.getElementById('cart-note');
  if (noteBox && !PREVIEW) {
    var noteTimer = null, noteSent = noteBox.value;
    var noteSave = function () {
      clearTimeout(noteTimer); noteTimer = null;
      if (noteBox.value === noteSent) return;
      noteSent = noteBox.value;
      fetch(ROOT + 'cart/update.js', {
        method: 'POST', keepalive: true,
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ note: noteSent })
      }).catch(function () {});
    };
    noteBox.addEventListener('input', function () { clearTimeout(noteTimer); noteTimer = setTimeout(noteSave, 600); });
    noteBox.addEventListener('change', noteSave);
    window.addEventListener('pagehide', noteSave);
  }
  function giftSet(g) { try { localStorage.setItem(GIFT_KEY, JSON.stringify(g)); } catch (e) {} }
  var giftTimer = null;
  function giftSave(g, from) {
    giftSet(g);
    giftPaint(from);
    clearTimeout(giftTimer);
    giftTimer = setTimeout(function () {
      var saved = function () {
        document.querySelectorAll('[data-gift-saved]').forEach(function (el) {
          el.hidden = !(g.msg || g.hide);
        });
        waWire();
      };
      if (PREVIEW) { saved(); return; }
      fetch(ROOT + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ attributes: { 'Gift message': g.msg, 'Gift: hide prices': g.hide ? 'Yes' : '' } })
      }).then(saved).catch(function () {});
    }, 500);
  }
  function giftPaint(from) {
    var g = giftGet();
    document.querySelectorAll('[data-gift]').forEach(function (box) {
      var ta = box.querySelector('[data-gift-msg]');
      if (ta && ta !== from && ta.value !== g.msg) ta.value = g.msg;
      var cb = box.querySelector('[data-gift-hide]');
      if (cb && cb !== from) cb.checked = !!g.hide;
      var c = box.querySelector('[data-gift-count]');
      if (c) c.textContent = (200 - (g.msg || '').length) + ' characters left';
      box.classList.toggle('has-note', !!(g.msg || g.hide));
      if ((g.msg || g.hide) && !box._opened) { box.open = true; box._opened = true; }
    });
  }
  function giftWire(box) {
    if (!box || box._wired) return;
    box._wired = true;
    var ta = box.querySelector('[data-gift-msg]');
    var cb = box.querySelector('[data-gift-hide]');
    if (ta) ta.addEventListener('input', function () {
      var g = giftGet(); g.msg = ta.value.slice(0, 200); giftSave(g, ta);
    });
    if (cb) cb.addEventListener('change', function () {
      var g = giftGet(); g.hide = cb.checked; giftSave(g, cb);
    });
  }
  document.querySelectorAll('[data-gift]').forEach(giftWire);
  if (!PREVIEW) {
    fetch(ROOT + 'cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (c) {
        var a = (c && c.attributes) || {};
        if ('Gift message' in a || 'Gift: hide prices' in a) {
          giftSet({ msg: a['Gift message'] || '', hide: a['Gift: hide prices'] === 'Yes' });
        }
        giftPaint();
      }).catch(function () { giftPaint(); });
  } else {
    giftPaint();
  }

  /* One line to a new quantity; 0 removes it. Shopify identifies a line by its
     key, the preview's in-memory cart by position. */
  function changeLine(idx, qty) {
    var item = lastCart && lastCart.items[idx];
    if (!item) return Promise.reject(new Error('That line has gone'));
    if (PREVIEW) {
      var f = fakeCart.items[idx];
      if (qty <= 0) {
        fakeCart.items.splice(idx, 1);
      } else if (f) {
        var each = f.price || Math.round(f.line_price / Math.max(1, f.quantity));
        f.quantity = qty;
        f.line_price = each * qty;
      }
      fakeCart.item_count = fakeCart.items.reduce(function (a, i) { return a + i.quantity; }, 0);
      fakeCart.total_price = fakeCart.items.reduce(function (a, i) { return a + i.line_price; }, 0);
      return Promise.resolve(fakeCart);
    }
    return fetch(ROOT + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: item.key, quantity: Math.max(0, qty) })
    }).then(function (r) {
      if (!r.ok) return r.json().then(function (j) { throw new Error(j.description || 'Could not update the cart'); });
      return getCart();
    });
  }

  /* Writes the COUNT, not the link. This used to replace the link's entire
     innerHTML, which rebuilt the label on every add and destroyed anything
     else inside it -- including the element the motion library animates. */
  function setCount(n) {
    /* `:not(.saved-link)` -- the saved-items heart shares the .cart-link class
       for its pill styling, and a bare selector rewrote the heart into a second
       "Cart (3)" button the first time the count changed. */
    document.querySelectorAll('.cart-link:not(.saved-link)').forEach(function (el) {
      var out = el.querySelector('[data-cart-count]');
      if (out) {
        out.textContent = n;
      } else {
        el.innerHTML = 'Cart (<span data-cart-count>' + n + '</span>)';
      }
      el.classList.toggle('has-items', n > 0);
    });
    /* every other place that prints the count -- the phone menu's "Cart (n)"
       link was server-rendered and stayed at the old number after an add */
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      if (el.textContent !== String(n)) el.textContent = n;
      /* an empty cart hides the bubble (theme.css [data-zero]) rather than
         showing a green 0 to every new visitor */
      el.toggleAttribute('data-zero', Number(n) === 0);
    });
    flyOnCount(Number(n));
  }

  /* ═══ fly to cart (2026-09-24, the user asked for it on every product) ═══
     When an add raises the count, a small copy of the product's picture
     travels to the cart icon that is on screen (the header bag, the phone tab
     bar's Cart, or the product page's sticky-bar bag) and the icon pops.
     It is started by a tap, never by scrolling or the pointer, lasts about
     0.7 s, and visitors who ask for reduced motion get only the pop. When the
     drawer is about to open, it waits for the picture to land (open() below),
     so the drawer does not cover the flight.
     Every add path is covered from one place: a capture-phase click (and the
     product form's submit) notes which pictures belong to the add, and the
     count going up is what sets them flying. */
  var FLY_BTN = '.quick, .qv-add, [data-add-lines], [data-rb2-add], [data-adv-add], [data-buybar-submit],' +
    ' [data-add-to-cart], [data-quick-add], .gcard-add, form[action*="/cart/add"] button[type="submit"]';
  var REDUCE_MOTION = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  var flyFrom = null, flyFromAt = 0, flyCount = null, flying = null;

  function onScreen(r) {
    return r.width > 0 && r.height > 0 && r.bottom > 0 && r.top < window.innerHeight &&
      r.right > 0 && r.left < window.innerWidth;
  }
  function pics(root, sel) {
    if (!root) return [];
    return [].slice.call(root.querySelectorAll(sel || 'img')).filter(function (i) {
      return i.currentSrc || i.src;
    });
  }
  // the pictures that belong to an add, with where they are right now
  function flySources(btn) {
    var imgs = [];
    var together = btn.closest('.together');
    var routine = btn.closest('[data-routine]');
    var qv = btn.closest('dialog');
    var card = btn.closest('[data-quick-id]');
    if (together) imgs = pics(together, '.together-item img');
    else if (routine) imgs = pics(routine, '[data-rb2-list] img');
    else if (qv) imgs = pics(qv).slice(0, 1);
    else if (card) imgs = pics(card).slice(0, 1);
    else if (btn.closest('form[action*="/cart/add"]') || btn.hasAttribute('data-buybar-submit')) {
      imgs = pics(document.querySelector('.product-media')).filter(function (i) {
        return onScreen(i.getBoundingClientRect());
      }).slice(0, 1);
      if (!imgs.length) imgs = pics(document.querySelector('.product-media')).slice(0, 1);
    } else {
      var box = btn;
      for (var k = 0; k < 4 && box && !imgs.length; k++) { box = box.parentElement; imgs = pics(box).slice(0, 1); }
    }
    return imgs.slice(0, 4).map(function (img) {
      var r = img.getBoundingClientRect();
      // a picture scrolled out of view flies from the button instead
      if (!onScreen(r)) r = btn.getBoundingClientRect();
      return { src: img.currentSrc || img.src, rect: r };
    });
  }
  function noteAdd(btn) {
    try { flyFrom = flySources(btn); flyFromAt = Date.now(); } catch (e) { flyFrom = null; }
  }
  document.addEventListener('click', function (e) {
    var b = e.target.closest ? e.target.closest(FLY_BTN) : null;
    if (b) noteAdd(b);
  }, true);
  // Enter in the quantity box submits the product form without a click
  document.addEventListener('submit', function (e) {
    var f = e.target;
    if (f && f.matches && f.matches('form[action*="/cart/add"]') && Date.now() - flyFromAt > 1000) {
      noteAdd(f.querySelector('button[type="submit"]') || f);
    }
  }, true);

  function cartTarget() {
    var cands = document.querySelectorAll('.bb-cart, [data-tab-cart], .cart-link:not(.saved-link)');
    for (var i = 0; i < cands.length; i++) {
      var r = cands[i].getBoundingClientRect();
      var cs = window.getComputedStyle(cands[i]);
      if (onScreen(r) && cs.visibility !== 'hidden' && cs.display !== 'none' && parseFloat(cs.opacity) > 0.1) {
        return cands[i];
      }
    }
    return null;
  }
  function pop(el) {
    /* the cart icon no longer bounces: the owner asked for it to stay still */
    return;
    if (!el || !el.animate) return;
    el.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.16)' }, { transform: 'scale(1)' }],
      { duration: 320, easing: 'ease-out' });
  }
  function fly(items) {
    var target = cartTarget();
    if (!target || !items || !items.length || REDUCE_MOTION || !document.body.animate) {
      pop(target);
      return Promise.resolve();
    }
    var t = target.getBoundingClientRect();
    var tx = t.left + t.width / 2, ty = t.top + t.height / 2;
    var runs = items.map(function (it, i) {
      var r = it.rect;
      var size = Math.max(44, Math.min(120, r.width, r.height));
      var x = r.left + r.width / 2 - size / 2, y = r.top + r.height / 2 - size / 2;
      var g = document.createElement('img');
      g.src = it.src;
      g.alt = '';
      g.setAttribute('aria-hidden', 'true');
      g.className = 'fly-ghost';
      g.style.cssText = 'position:fixed;left:' + x + 'px;top:' + y + 'px;width:' + size + 'px;height:' + size + 'px;' +
        'object-fit:cover;border-radius:14px;background:#fff;box-shadow:0 8px 24px rgba(0,0,0,.18);' +
        'z-index:2147483000;pointer-events:none;margin:0';
      document.body.appendChild(g);
      var dx = tx - (x + size / 2), dy = ty - (y + size / 2);
      var anim = g.animate([
        { transform: 'translate(0,0) scale(1)', opacity: 1 },
        { transform: 'translate(' + dx * 0.45 + 'px,' + (dy * 0.45 - 48) + 'px) scale(.62)', opacity: 1, offset: 0.55 },
        { transform: 'translate(' + dx + 'px,' + dy + 'px) scale(.14)', opacity: 0.35 }
      ], { duration: 700, delay: i * 110, easing: 'cubic-bezier(.45,0,.25,1)', fill: 'forwards' });
      return new Promise(function (resolve) {
        var done = function () { if (g.parentNode) g.parentNode.removeChild(g); resolve(); };
        anim.onfinish = done;
        anim.oncancel = done;
        setTimeout(done, 700 + i * 110 + 400);   // never hold the drawer back
      });
    });
    return Promise.all(runs).then(function () { pop(target); });
  }
  function flyOnCount(n) {
    var was = flyCount;
    flyCount = n;
    if (was === null || n <= was || !flyFrom || Date.now() - flyFromAt > 8000) return;
    var items = flyFrom;
    flyFrom = null;
    flying = fly(items);
    flying.then(function () { flying = null; });
  }
  // the count the page was served with, so the first add is seen as a rise
  (function () {
    var el = document.querySelector('[data-cart-count]');
    flyCount = el ? (parseInt(el.textContent, 10) || 0) : 0;
  })();

  /* ═══ toast, for quick-add when a drawer is heavy-handed ════════════ */
  var toastEl, toastTimer;
  function toast(msg, actionLabel, action) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'toast';
      toastEl.setAttribute('role', 'status');
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = '';
    var t = document.createElement('span');
    t.textContent = msg;
    toastEl.appendChild(t);
    if (actionLabel && action) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'toast-act';
      b.textContent = actionLabel;
      b.addEventListener('click', function () {
        toastEl.classList.remove('is-open');
        action();
      }, { once: true });
      toastEl.appendChild(b);
    }
    toastEl.classList.add('is-open');
    clearTimeout(toastTimer);
    /* an Undo needs time to be read and reached */
    toastTimer = setTimeout(function () { toastEl.classList.remove('is-open'); },
      actionLabel ? 6000 : 2600);
  }

  /* ═══ the cart API ══════════════════════════════════════════════════ */
  var fakeCart = { items: [], total_price: 0, item_count: 0 };
  // The static preview's cart page renders its rows server-side, so seed the
  // fake cart from the same data or the page would show lines while the script
  // believed the cart was empty. Guarded by PREVIEW; the real theme reads
  // /cart.js and never looks at this.
  if (PREVIEW && window.GLORAMOUS_PREVIEW_CART) {
    fakeCart = window.GLORAMOUS_PREVIEW_CART;
  }

  function addToCart(id, qty, meta) {
    if (PREVIEW) {
      var found = fakeCart.items.filter(function (i) { return i.id === id; })[0];
      if (found) {
        found.quantity += qty;
        found.line_price = found.price * found.quantity;
      } else {
        fakeCart.items.push({
          id: id, quantity: qty, price: meta.price,
          line_price: meta.price * qty, product_title: meta.title,
          variant_title: meta.variant, image: meta.image
        });
      }
      fakeCart.item_count = fakeCart.items.reduce(function (a, i) { return a + i.quantity; }, 0);
      fakeCart.total_price = fakeCart.items.reduce(function (a, i) { return a + i.line_price; }, 0);
      return Promise.resolve(fakeCart);
    }
    return fetch(ROOT + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: id, quantity: qty })
    }).then(function (r) {
      if (!r.ok) return r.json().then(function (j) { throw new Error(j.description || 'Could not add that'); });
      return fetch(ROOT + 'cart.js', { headers: { 'Accept': 'application/json' } }).then(function (c) { return c.json(); });
    }).then(function (cart) {
      return {
        items: cart.items.map(function (i) {
          return {
            id: i.id, key: i.key, price: i.final_price,
            quantity: i.quantity, line_price: i.final_line_price,
            product_title: i.product_title, product_type: i.product_type, variant_title: i.variant_title,
            image: i.image ? i.image.replace(/(\.[a-z]+)(\?|$)/i, '_120x$1$2') : null
          };
        }),
        total_price: cart.total_price,
        item_count: cart.item_count
      };
    });
  }

  function getCart() {
    if (PREVIEW) return Promise.resolve(fakeCart);
    return fetch(ROOT + 'cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        return {
          items: cart.items.map(function (i) {
            return {
              id: i.id, key: i.key, price: i.final_price,
              quantity: i.quantity, line_price: i.final_line_price,
              product_title: i.product_title, product_type: i.product_type, variant_title: i.variant_title,
              image: i.image ? i.image.replace(/(\.[a-z]+)(\?|$)/i, '_120x$1$2') : null
            };
          }),
          total_price: cart.total_price,
          item_count: cart.item_count
        };
      });
  }

  /* ═══ the cart link opens the drawer rather than leaving the page ════ */
  /* not the heart: it must go to the saved page, not open the cart */
  document.querySelectorAll('.cart-link:not(.saved-link), .bb-cart').forEach(function (link) {
    link.addEventListener('click', function (e) {
      // let modified clicks and middle clicks reach the real cart page
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      e.preventDefault();
      getCart().then(function (cart) {
        render(cart);
        setCount(cart.item_count);
        open('Cart');
      }).catch(function () { location.href = link.getAttribute('href') || '/cart'; });
    });
  });

  /* ═══ product form: add without leaving the page ════════════════════ */
  var pform = document.querySelector('form[action*="/cart/add"]');
  if (pform) {
    pform.addEventListener('submit', function (e) {
      var idField = pform.querySelector('[name="id"]');
      if (!idField) return;                       // let it post normally
      e.preventDefault();
      var btn = pform.querySelector('button[type="submit"]');
      var was = btn ? btn.textContent : '';
      if (btn) { btn.disabled = true; btn.textContent = 'Adding…'; }

      var qty = parseInt((pform.querySelector('[name="quantity"]') || {}).value, 10) || 1;
      // the selected variant button wins, else the form's own price
      var sel = document.querySelector('[data-opts] button[aria-pressed="true"]');
      var box = pform.closest('[data-price-cents]');
      var cents = parseInt(
        (sel && sel.getAttribute('data-price-cents')) ||
        (box && box.getAttribute('data-price-cents')) || '0', 10) || 0;
      // A single-variant product has no option buttons to read, so the size
      // came through blank and the WhatsApp order line said "1 x Vitamin C
      // Serum" with no volume — which is the one thing the person picking the
      // order needs. The page's own JSON record already carries it.
      var recSize = '';
      var recNode2 = document.querySelector('[data-recent-record]');
      if (recNode2) {
        try {
          var r2 = JSON.parse(recNode2.textContent);
          if (r2 && r2.size && r2.size !== 'Default Title') recSize = r2.size;
        } catch (e) {}
      }
      var meta = {
        title: (document.querySelector('.product-info h1') || {}).textContent || 'Product',
        /* the attribute, not textContent: the size buttons now carry a price
           line as well, and the order would have read "280 mL$12" */
        variant: sel ? (sel.getAttribute('data-size') || sel.textContent.trim()) : recSize,
        price: cents,
        image: (document.querySelector('.product-media img') || {}).src || null
      };

      addToCart(idField.value, qty, meta)
        .then(function (cart) {
          setCount(cart.item_count);
          render(cart);
          open('Added to cart');
        })
        .catch(function (err) { toast(err.message || 'Could not add that'); })
        .then(function () { if (btn) { btn.disabled = false; btn.textContent = was; } });
    });
  }

  /* ═══ quick add from any grid card ══════════════════════════════════ */
  document.querySelectorAll('[data-quick-id]').forEach(function (card) {
    var id = card.getAttribute('data-quick-id');
    if (!id) return;                              // multi-variant: link through instead
    var host = card.querySelector('.card-img,.fcard-img,.big-img');
    if (!host) return;

    var b = document.createElement('button');
    b.className = 'quick';
    b.type = 'button';
    b.textContent = 'Quick add';
    host.appendChild(b);

    b.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      b.disabled = true;
      b.textContent = 'Adding…';
      addToCart(id, 1, {
        title: (card.querySelector('h2,h3') || {}).textContent || 'Product',
        variant: card.getAttribute('data-quick-variant') || '',
        price: parseInt(card.getAttribute('data-price') || '0', 10),
        image: (card.querySelector('img') || {}).src || null
      }).then(function (cart) {
        setCount(cart.item_count);
        render(cart);
        b.textContent = 'Added';
        toast((card.querySelector('h2,h3') || {}).textContent + ' added to cart');
        setTimeout(function () { b.disabled = false; b.textContent = 'Quick add'; }, 1600);
      }).catch(function (err) {
        toast(err.message || 'Could not add that');
        b.disabled = false;
        b.textContent = 'Quick add';
      });
    });
  });

  /* ═══ quick view: sizes and Add to cart without leaving the grid ═══════
     Every card gets a second button beside Quick add. A card with sizes gets
     "Choose size" instead, because Quick add cannot know which size to add.
     It opens a native <dialog> (focus trap, Escape and the inert page come
     free) filled from the live product: /products/<handle>.js on Shopify, the
     preview's quickview.json here. Adding reuses the same cart call as every
     other button and hands over to the drawer. */
  var qvDlg = null, qvState = null, qvPreviewData = null;

  function qvEsc(t) {
    return String(t == null ? '' : t).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  function qvLoad(href) {
    var clean = href.split('#')[0].split('?')[0];
    if (PREVIEW) {
      qvPreviewData = qvPreviewData || fetch('quickview.json').then(function (r) { return r.json(); });
      return qvPreviewData.then(function (map) {
        var d = map[clean.split('/').pop()];
        if (!d) throw new Error('No details for that product');
        return d;
      });
    }
    return fetch(clean.replace(/\/$/, '') + '.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error('Could not load that product'); return r.json(); })
      .then(function (p) {
        var img = p.featured_image || null;
        if (img && img.indexOf('//') === 0) img = 'https:' + img;
        var text = String(p.description || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
        return {
          title: p.title, url: clean, type: p.type, image: img,
          description: text.split('. ')[0] + (text.indexOf('. ') > -1 ? '.' : ''),
          variants: p.variants.map(function (v) {
            return { id: v.id, title: v.title, price: v.price,
                     compare_at_price: v.compare_at_price || 0, available: v.available };
          })
        };
      });
  }

  function qvPrice(v) {
    if (v.compare_at_price && v.compare_at_price > v.price) {
      return '<s>' + money(v.compare_at_price) + '</s> <ins>' + money(v.price) + '</ins>';
    }
    return money(v.price);
  }

  function qvBuild() {
    if (qvDlg) return;
    qvDlg = document.createElement('dialog');
    qvDlg.className = 'qv';
    qvDlg.setAttribute('aria-labelledby', 'qv-title');
    qvDlg.innerHTML =
      '<div class="qv-inner">' +
        '<button class="qv-close" type="button" aria-label="Close quick view">&times;</button>' +
        '<div class="qv-nav">' +
          '<button class="qv-step" type="button" data-qv-go="-1" aria-label="Previous product">' +
            '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
            'stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M15 5l-7 7 7 7"/></svg></button>' +
          '<button class="qv-step" type="button" data-qv-go="1" aria-label="Next product">' +
            '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
            'stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M9 5l7 7-7 7"/></svg></button>' +
        '</div>' +
        '<div class="qv-media"><img alt="" width="600" height="600"></div>' +
        '<div class="qv-info">' +
          '<p class="qv-type"></p>' +
          '<h2 id="qv-title" class="qv-title"></h2>' +
          '<div class="qv-price" aria-live="polite"></div>' +
          '<p class="qv-desc"></p>' +
          '<div class="qv-opts-wrap"><span class="opt-label">Size</span>' +
            '<div class="opts qv-opts" role="group" aria-label="Size"></div></div>' +
          '<div class="qv-buy">' +
            '<div class="stepper">' +
              '<button type="button" data-qv-step="-1" aria-label="Decrease quantity">&minus;</button>' +
              '<input type="number" value="1" min="1" aria-label="Quantity">' +
              '<button type="button" data-qv-step="1" aria-label="Increase quantity">+</button>' +
            '</div>' +
            '<button class="btn qv-add" type="button">Add to cart</button>' +
          '</div>' +
          '<a class="qv-full" href="#">See full details</a>' +
        '</div>' +
      '</div>';
    document.body.appendChild(qvDlg);

    qvDlg.querySelector('.qv-close').addEventListener('click', function () { qvDlg.close(); });
    qvDlg.querySelector('.qv-nav').addEventListener('click', function (e) {
      var b = e.target.closest ? e.target.closest('[data-qv-go]') : null;
      if (b) qvGo(parseInt(b.getAttribute('data-qv-go'), 10));
    });
    /* arrow keys browse, unless the quantity field has focus */
    qvDlg.addEventListener('keydown', function (e) {
      if (e.target && e.target.tagName === 'INPUT') return;
      if (e.key === 'ArrowRight') { e.preventDefault(); qvGo(1); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); qvGo(-1); }
    });
    // a click on the backdrop closes it; a click inside the panel does not
    qvDlg.addEventListener('click', function (e) {
      if (e.target === qvDlg) { qvDlg.close(); return; }
      var st = e.target.closest ? e.target.closest('[data-qv-step]') : null;
      if (st) {
        var inp = qvDlg.querySelector('.qv-buy input');
        inp.value = Math.max(1, (parseInt(inp.value, 10) || 1) + parseInt(st.getAttribute('data-qv-step'), 10));
      }
    });
    qvDlg.querySelector('.qv-opts').addEventListener('click', function (e) {
      var b = e.target.closest ? e.target.closest('button[data-i]') : null;
      if (!b || !qvState) return;
      qvState.i = parseInt(b.getAttribute('data-i'), 10);
      qvPaint();
    });
    qvDlg.querySelector('.qv-add').addEventListener('click', function () {
      if (!qvState) return;
      var d = qvState.d, v = d.variants[qvState.i];
      var btn = this;
      var qty = parseInt(qvDlg.querySelector('.qv-buy input').value, 10) || 1;
      btn.disabled = true;
      btn.textContent = 'Adding…';
      addToCart(v.id, qty, {
        title: d.title,
        variant: v.title === 'Default Title' ? '' : v.title,
        price: v.price, image: d.image
      }).then(function (cart) {
        setCount(cart.item_count);
        render(cart);
        qvDlg.close();
        open('Cart');
        toast(d.title + ' added to cart');
      }).catch(function (err) {
        toast(err.message || 'Could not add that');
      }).then(function () {
        btn.disabled = false;
        btn.textContent = 'Add to cart';
      });
    });
  }

  /* The cards a quick view can step through: the visible ones in the same
     grid as the card it was opened from, in the order they are shown -- so a
     filtered, sorted catalogue browses in its filtered, sorted order. */
  function qvSiblings(card) {
    if (!card || !card.parentElement) return [];
    return [].filter.call(card.parentElement.children, function (c) {
      return c.matches('a.card, a.fcard, a.big') && !c.hidden && c.getClientRects().length;
    });
  }

  var qvBusy = false;
  function qvGo(dir) {
    if (!qvState || qvBusy) return;
    var list = qvSiblings(qvState.card);
    if (list.length < 2) return;
    var at = list.indexOf(qvState.card);
    var next = list[(at + dir + list.length) % list.length];
    qvBusy = true;
    var inner = qvDlg.querySelector('.qv-inner');
    inner.classList.add('is-swapping');
    inner.setAttribute('data-dir', dir > 0 ? 'next' : 'prev');
    qvLoad(next.getAttribute('href')).then(function (d) {
      setTimeout(function () {
        qvState = { d: d, i: 0, card: next };
        qvDlg.querySelector('.qv-buy input').value = 1;
        qvPaint();
        inner.classList.remove('is-swapping');
        qvBusy = false;
      }, CALM ? 0 : 150);
    }).catch(function () {
      inner.classList.remove('is-swapping');
      qvBusy = false;
    });
  }

  function qvPaint() {
    var d = qvState.d, v = d.variants[qvState.i];
    var multi = d.variants.length > 1;
    var img = qvDlg.querySelector('.qv-media img');
    img.src = d.image || '';
    img.alt = d.title;
    qvDlg.querySelector('.qv-type').textContent = d.type || '';
    qvDlg.querySelector('.qv-title').textContent = d.title;
    roll(qvDlg.querySelector('.qv-price'), qvPrice(v) +
      (!multi && v.title !== 'Default Title' ? ' <span class="qv-size">' + qvEsc(v.title) + '</span>' : ''));
    var desc = qvDlg.querySelector('.qv-desc');
    desc.textContent = d.description || '';
    desc.hidden = !d.description;
    qvDlg.querySelector('.qv-opts-wrap').hidden = !multi;
    qvDlg.querySelector('.qv-opts').innerHTML = multi ? d.variants.map(function (x, i) {
      return '<button type="button" data-i="' + i + '" aria-pressed="' + (i === qvState.i) + '"' +
        (x.available === false ? ' disabled' : '') + '>' + qvEsc(x.title) +
        '<small class="opt-price">' + money(x.price).replace(/\.00$/, '') + '</small></button>';
    }).join('') : '';
    if (multi && window.GloramousGlide) window.GloramousGlide(qvDlg.querySelector('.qv-opts'));
    var nav = qvDlg.querySelector('.qv-nav');
    if (nav) nav.hidden = qvSiblings(qvState.card).length < 2;
    qvDlg.querySelector('.qv-full').href = d.url;
    var add = qvDlg.querySelector('.qv-add');
    add.disabled = v.available === false;
    add.textContent = v.available === false ? 'Sold out' : 'Add to cart';
  }

  document.querySelectorAll('a.card, a.fcard, a.big').forEach(function (card) {
    var href = card.getAttribute('href');
    var host = card.querySelector('.card-img,.fcard-img,.big-img');
    if (!href || !host || host.querySelector('.qv-open')) return;
    // professional products are requested on WhatsApp, not bought: no quick view
    if (card.hasAttribute('data-pro') || card.getAttribute('data-price') === '0') return;
    var row = document.createElement('div');
    row.className = 'quick-row';
    var existing = host.querySelector('.quick');
    if (existing) row.appendChild(existing);
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'quick qv-open' + (existing ? ' quick--ghost' : '');
    b.textContent = existing ? 'Quick view' : 'Choose size';
    row.appendChild(b);
    host.appendChild(row);

    b.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      var was = b.textContent;
      b.textContent = 'Opening…';
      qvLoad(href).then(function (d) {
        qvBuild();
        qvState = { d: d, i: 0, card: card };
        qvDlg.querySelector('.qv-buy input').value = 1;
        qvPaint();
        if (typeof qvDlg.showModal === 'function') qvDlg.showModal();
        else qvDlg.setAttribute('open', '');
      }).catch(function () {
        location.href = href;                      // no data: the product page still works
      }).then(function () { b.textContent = was; });
    });
  });

  /* ═══ order on WhatsApp ════════════════════════════════════════════════
     Lebanon has no Shopify Payments, so for this shop WhatsApp is not a
     nicety — it is likely the main way orders actually arrive. Until now the
     button opened an EMPTY chat and the customer had to retype their order
     from memory, which is where orders get lost or mis-picked.

     This composes the message from the live cart:

       - itemised lines with quantity, variant and line price
       - the total
       - blank Name / Address / Phone lines, because the merchant needs those
         and a customer filling in labelled blanks is far more reliable than
         one being asked three follow-up questions
       - a cart permalink, so the merchant (or the customer) can rebuild the
         exact cart and check out normally if they change their mind

     Progressive enhancement: the anchor already points at a plain wa.me link,
     so with scripting off it still opens a chat — just without the order in
     it. The href is only upgraded once the cart has actually been read. */
  var WA_MAX = 1400;          // keep the whole URL well inside browser limits

  function waCompose(cart, opts) {
    var lines = [];
    if (opts.intro) lines.push(opts.intro, '');

    var items = cart.items || [];
    var listed = 0, skipped = 0, body = [];
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      var variant = (it.variant_title && it.variant_title !== 'Default Title')
        ? ' (' + it.variant_title + ')' : '';
      var row = '• ' + it.quantity + ' x ' + it.product_title + variant +
                ' — ' + money(it.line_price);
      // a 30-line cart would blow past the URL limit and WhatsApp would
      // silently truncate mid-word, so cut cleanly and say so
      if (body.join('\n').length + row.length > WA_MAX) { skipped++; continue; }
      body.push(row);
      listed++;
    }
    lines = lines.concat(body);
    if (skipped) {
      // only promise a cart link when there actually is one to follow
      lines.push('• and ' + skipped + ' more item' + (skipped === 1 ? '' : 's') +
                 (opts.permalink ? ' — full list in the cart link below'
                                 : ' — please ask us for the full list'));
    }

    if (FEE) {
      lines.push('', 'Subtotal: ' + money(cart.total_price),
                 'Delivery: ' + money(FEE),
                 'Total: ' + money(cart.total_price + FEE));
    } else {
      lines.push('', 'Total: ' + money(cart.total_price));
    }
    var gift = giftGet();
    if (gift.msg || gift.hide) {
      lines.push('', 'Gift note: ' + (gift.msg || '(no message)'));
      if (gift.hide) lines.push('This is a gift, please hide the prices.');
    }

    if (opts.permalink) lines.push('', 'Cart: ' + opts.permalink);

    // the delivery details the shopper filled in once (assets/theme.js)
    var d = window.GloramousDelivery ? window.GloramousDelivery() : {};
    lines.push('', 'Name: ' + (d.name || ''), 'Area: ' + (d.area || ''), 'Address: ' + (d.address || ''), 'Phone: ' + (d.phone || ''));
    return lines.join('\n');
  }

  /* /cart/<variant>:<qty>,<variant>:<qty> rebuilds a cart exactly. Only
     meaningful with real Shopify variant ids, so it is skipped in preview. */
  function waPermalink(cart) {
    if (PREVIEW || !cart.items || !cart.items.length) return '';
    var parts = [];
    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i];
      if (!it.id) return '';
      parts.push(it.id + ':' + it.quantity);
    }
    return location.origin + '/cart/' + parts.join(',');
  }

  /* Write the composed order into every WhatsApp anchor on the page. Called
     after each cart render, so the anchor is a plain correct link at all times
     and the click needs no interception — nothing for a popup blocker to stop. */
  function waApply(cart) {
    var els = document.querySelectorAll('[data-wa-order]');
    if (!els.length) return;
    var link = cart && cart.items && cart.items.length
      ? null : '';                      // '' means: fall back to the bare number
    els.forEach(function (el) {
      var phone = el.getAttribute('data-wa-order');
      if (!phone) return;
      if (link === '') {
        el.setAttribute('href', 'https://wa.me/' + phone);
        return;
      }
      var text = waCompose(cart, {
        intro: el.getAttribute('data-wa-intro') || '',
        permalink: waPermalink(cart)
      });
      el.setAttribute('href',
        'https://wa.me/' + phone + '?text=' + encodeURIComponent(text));
    });
  }

  function waWire() {
    // On the cart page the cart is already known to the server, but the anchor
    // still needs composing on load; elsewhere this fills it in for whatever
    // is in the cart when the page opens.
    if (!document.querySelector('[data-wa-order]')) return;
    getCart().then(waApply).catch(function () { /* bare wa.me href stands */ });
  }

  waWire();
  window.GloramousWaRefresh = waWire;

  /* ═══ header search ════════════════════════════════════════════════════
     The form posts to /search on its own, so this is enhancement only.

     Suggestions come from templates/search.suggest.liquid rather than
     Shopify's /search/suggest.json, because that endpoint does not return
     tags — and without tags the professional-product gate cannot be applied,
     which would leak clinic-only products into the header search of a
     logged-out visitor. See the comment in that template.

     In PREVIEW there is no Shopify, so it filters a static index instead. */
  var sOpen = document.querySelector('[data-search-open]');
  var sPanel = document.querySelector('[data-search-panel]');

  if (sOpen && sPanel) {
    var sInput = sPanel.querySelector('[data-search-input]');
    var sOut = sPanel.querySelector('[data-search-results]');
    var sHint = sPanel.querySelector('[data-search-hint]');
    var sTimer, sSeq = 0, sIndex = null, sCursor = -1;

    function sShow() {
      sPanel.hidden = false;
      sOpen.setAttribute('aria-expanded', 'true');
      // Force a reflow so the transition has a start state to animate from.
      // requestAnimationFrame would read better but does not fire while the
      // document is hidden, which leaves the panel unhidden and 0px tall.
      void sPanel.offsetHeight;
      sPanel.classList.add('is-open');
      sInput.focus();
      sInput.select();
      if (!sInput.value.trim()) sqShow();
    }

    function sHide() {
      sPanel.classList.remove('is-open');
      sOpen.setAttribute('aria-expanded', 'false');
      sInput.setAttribute('aria-expanded', 'false');
      sCursor = -1;
      // keep it in the DOM until the transition ends, then take it out of the
      // tab order — a hidden-but-focusable panel is a keyboard trap
      setTimeout(function () {
        if (!sPanel.classList.contains('is-open')) sPanel.hidden = true;
      }, 200);
      sOpen.focus();
    }

    sOpen.addEventListener('click', function () {
      if (sPanel.classList.contains('is-open')) { sHide(); } else { sShow(); }
    });
    sPanel.querySelector('[data-search-close]').addEventListener('click', sHide);

    document.addEventListener('keydown', function (e) {
      // "/" is the search shortcut everywhere it is not being typed into
      var t = e.target, tag = t && t.tagName;
      var typing = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' ||
                   (t && t.isContentEditable);
      if (e.key === '/' && !typing && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        sShow();
        return;
      }
      if (e.key === 'Escape' && sPanel.classList.contains('is-open')) sHide();
    });

    /* recent searches — shown when the panel opens with an empty box.
       On a 121-product catalogue people search the same three things
       repeatedly; retyping "hyaluronic" is the sort of small friction that
       makes a site feel unhelpful. Stored in the visitor's own browser. */
    var SQ_KEY = 'gloramous.searches';

    function sqRead() {
      try {
        var raw = JSON.parse(localStorage.getItem(SQ_KEY) || '[]');
        return Object.prototype.toString.call(raw) === '[object Array]' ? raw : [];
      } catch (e) { return []; }
    }

    function sqSave(term) {
      if (!term || term.length < 2) return;
      var cur = sqRead().filter(function (t) { return t.toLowerCase() !== term.toLowerCase(); });
      cur.unshift(term);
      try { localStorage.setItem(SQ_KEY, JSON.stringify(cur.slice(0, 6))); } catch (e) {}
    }

    function sqShow() {
      var past = sqRead();
      var pop = window.GloramousAdvisor ? window.GloramousAdvisor.popular : [];
      var popHtml = pop.length ? '<div class="sq sq--pop"><span class="sq-label">Search by skin need</span>' +
        pop.map(function (t) { return '<button class="chip" type="button" data-sq="' + esc(t) + '">' + esc(t) + '</button>'; }).join('') + '</div>' : '';
      if (!past.length) { sOut.innerHTML = popHtml; if (sHint) sHint.hidden = !!popHtml; return; }
      sOut.innerHTML = popHtml +
        '<div class="sq"><span class="sq-label">Recent</span>' +
        past.map(function (t) {
          return '<button class="chip" type="button" data-sq="' + esc(t) + '">' +
                 esc(t) + '</button>';
        }).join('') +
        '<button class="sq-clear" type="button" data-sq-clear>Clear</button></div>';
      if (sHint) sHint.hidden = true;
    }

    sOut.addEventListener('click', function (e) {
      var t = e.target.closest ? e.target.closest('[data-sq]') : null;
      if (t) {
        sInput.value = t.getAttribute('data-sq');
        sInput.dispatchEvent(new Event('input'));
        return;
      }
      if (e.target.closest && e.target.closest('[data-sq-clear]')) {
        try { localStorage.removeItem(SQ_KEY); } catch (err) {}
        sOut.innerHTML = '';
        if (sHint) sHint.hidden = false;
      }
    });

    /* remember a term once it has actually produced something, not on every
       keystroke — otherwise "h", "hy", "hya" all end up in the list */
    var sqTimer;
    sInput.addEventListener('input', function () {
      clearTimeout(sqTimer);
      var v = sInput.value.trim();
      if (v.length < 2) return;
      sqTimer = setTimeout(function () {
        if (sOut.querySelector('.sres')) sqSave(v);
      }, 1200);
    });

    sPanel.querySelector('.searchform').addEventListener('submit', function () {
      sqSave(sInput.value.trim());
    });

    /* results list keyboard handling: down/up move, Enter follows */
    function sRows() { return sOut.querySelectorAll('.sres'); }

    function sMove(delta) {
      var rows = sRows();
      if (!rows.length) return;
      sCursor += delta;
      if (sCursor < 0) sCursor = rows.length - 1;
      if (sCursor >= rows.length) sCursor = 0;
      for (var i = 0; i < rows.length; i++) {
        rows[i].setAttribute('aria-selected', i === sCursor ? 'true' : 'false');
        rows[i].classList.toggle('is-cursor', i === sCursor);
      }
      rows[sCursor].scrollIntoView({ block: 'nearest' });
    }

    sInput.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowDown') { e.preventDefault(); sMove(1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); sMove(-1); }
      else if (e.key === 'Enter' && sCursor > -1) {
        var row = sRows()[sCursor];
        if (row) { e.preventDefault(); location.href = row.getAttribute('href'); }
      }
    });

    /* The letters that matched, marked. Built on the ESCAPED title so the
       marks can never open a hole for markup in a product name. */
    function mark(title, term) {
      var out = esc(title);
      var words = String(term || '').trim().split(/\s+/).filter(function (w) { return w.length > 1; });
      words.forEach(function (w) {
        var safe = esc(w).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        out = out.replace(new RegExp('(' + safe + ')(?![^<]*>)', 'ig'), '<mark>$1</mark>');
      });
      return out;
    }

    function sRender(list, term, extra) {
      sCursor = -1;
      extra = extra || {};
      if (!list.length) {
        sOut.innerHTML = '<p class="sres-empty">Nothing matches &ldquo;' +
          esc(term) + '&rdquo;. Try a shorter word, or an ingredient.</p>' +
          (extra.suggest ? '<p class="sres-dym">Did you mean <button class="chip" type="button" data-sq="' + esc(extra.suggest) + '">' + esc(extra.suggest) + '</button></p>' : '');
        sInput.setAttribute('aria-expanded', 'false');
        if (sHint) sHint.hidden = true;
        return;
      }
      sOut.innerHTML = list.map(function (r) {
        var img = r.image
          ? '<img src="' + esc(r.image) + '" alt="" width="46" height="46" loading="lazy">'
          : '<span class="fam-ph">&middot;</span>';
        var price = (typeof r.price === 'number' && r.price > 0)
          ? money(r.price) : 'Request to order';
        /* a $0 row is request-to-order (professional), never "Sold out" */
        if (r.available === false && r.price > 0) price = 'Sold out';
        /* a professional product someone searched for by name: shown, locked,
           and leading to the account request rather than to nothing */
        if (r.locked) {
          return '<a class="sres is-locked" role="option" aria-selected="false" href="' + esc(r.request_url || r.url) + '">' +
            '<span class="sres-img">' + img + '</span>' +
            '<span class="sres-text"><b>' + mark(r.title, term) + '</b>' +
            '<span>Professional only. Request access</span></span>' +
            '<span class="sres-price sres-lock" aria-hidden="true"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><rect x="5" y="11" width="14" height="9" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/></svg></span></a>';
        }
        var pro = r.pro ? '<span class="pro">Pro</span>' : '';
        var size = (r.size && r.size !== 'Default Title') ? '  ' + esc(r.size) : '';
        return '<a class="sres" role="option" aria-selected="false" href="' + esc(r.url) + '">' +
          '<span class="sres-img">' + img + '</span>' +
          '<span class="sres-text"><b>' + mark(r.title, term) + pro + '</b>' +
          '<span>' + esc(r.type || '') + size + '</span></span>' +
          '<span class="sres-price">' + price + '</span></a>';
      }).join('');
      if (extra.advice) sOut.innerHTML = extra.advice + '<p class="sres-sub">' + esc(term) + '</p>' + sOut.innerHTML;
      sInput.setAttribute('aria-expanded', 'true');
      if (sHint) sHint.hidden = true;
    }

    /* PREVIEW: one static index, filtered in the browser */
    function sPreviewSearch(term) {
      function run() {
        // Arabic words map to the catalogue's English words
        var terms = window.GloramousSearchTerms ? window.GloramousSearchTerms(term) : [term.toLowerCase()];
        var hits = sIndex.filter(function (r) {
          var hay = r.hay || '';
          return terms.some(function (q) { return hay.indexOf(q) > -1; });
        }).sort(function (a, b) { return (a.locked ? 1 : 0) - (b.locked ? 1 : 0); }).slice(0, 8);
        sRender(hits, term);
      }
      if (sIndex) { run(); return; }
      fetch('search-index.json')
        .then(function (r) { return r.json(); })
        .then(function (j) { sIndex = j.results || j; run(); })
        .catch(function () { sRender([], term); });
    }

    function sQuery(term) {
      var mine = ++sSeq;
      /* the advisor ranks the whole catalogue in the browser and understands
         skin needs; the older per-keystroke query stays as the fallback */
      if (window.GloramousAdvisor) {
        window.GloramousAdvisor.search(term).then(function (res) {
          if (mine !== sSeq) return;
          sRender(res.list, term, res);
        }).catch(function () {
          if (mine !== sSeq) return;
          if (PREVIEW) sPreviewSearch(term); else sLegacy(term, mine);
        });
        return;
      }
      if (PREVIEW) { sPreviewSearch(term); return; }
      sLegacy(term, mine);
    }

    function sLegacy(term, mine) {
      var sTerms = window.GloramousSearchTerms ? window.GloramousSearchTerms(term) : [term];
      fetch(ROOT + 'search?view=suggest&type=product&q=' + encodeURIComponent(sTerms.join(' OR ')),
            { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (j) {
          // a slow early request must not overwrite a later keystroke's results
          if (mine !== sSeq) return;
          sRender(j.results || [], term);
        })
        .catch(function () {
          if (mine === sSeq) sRender([], term);
        });
    }

    sInput.addEventListener('input', function () {
      var term = sInput.value.trim();
      clearTimeout(sTimer);
      if (term.length < 2) {
        sInput.setAttribute('aria-expanded', 'false');
        sOut.innerHTML = '';
        sqShow();                       // fall back to the recent list
        return;
      }
      // long enough to stop firing on every letter, short enough to feel live
      sTimer = setTimeout(function () { sQuery(term); }, 180);
    });

    document.addEventListener('click', function (e) {
      if (!sPanel.classList.contains('is-open')) return;
      if (sPanel.contains(e.target) || sOpen.contains(e.target)) return;
      sHide();
    });
  }

  /* ═══ recently viewed ══════════════════════════════════════════════════
     Kept in the visitor's own browser and nowhere else. The product page
     prints a JSON record of itself; this reads it, prepends it to the list,
     and renders the list into the section shell wherever that shell exists.

     Every localStorage call is wrapped: it throws outright in a locked-down
     browser, and returns garbage if someone has hand-edited the key. A broken
     strip must not take the cart down with it. */
  var RV_KEY = 'gloramous.recent', RV_MAX = 12, RV_SHOW = 5;

  function rvRead() {
    try {
      var raw = JSON.parse(localStorage.getItem(RV_KEY) || '[]');
      return Object.prototype.toString.call(raw) === '[object Array]' ? raw : [];
    } catch (e) { return []; }
  }

  function rvWrite(list) {
    try { localStorage.setItem(RV_KEY, JSON.stringify(list.slice(0, RV_MAX))); }
    catch (e) { /* private window, or quota — the strip just stays empty */ }
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  var rvHere = null;
  var rvNode = document.querySelector('[data-recent-record]');
  if (rvNode) {
    try {
      var rec = JSON.parse(rvNode.textContent);
      if (rec && rec.handle && rec.url) {
        rvHere = rec.handle;
        var list = rvRead().filter(function (i) {
          return i && i.handle && i.handle !== rec.handle;
        });
        list.unshift(rec);
        rvWrite(list);
      }
    } catch (e) { /* malformed record: leave the stored list alone */ }
  }

  var rvHost = document.querySelector('[data-recent]');
  if (rvHost) {
    var rvRow = rvHost.querySelector('[data-recent-row]');
    // never offer to take you back to the page you are standing on
    var rvItems = rvRead().filter(function (i) {
      return i && i.handle && i.url && i.handle !== rvHere;
    }).slice(0, RV_SHOW);

    if (rvItems.length && rvRow) {
      rvRow.innerHTML = rvItems.map(function (i) {
        var img = i.image
          ? '<img src="' + esc(i.image) + '" alt="" width="120" height="120" loading="lazy">'
          : '<span class="fam-ph">&middot;</span>';
        var sub = (typeof i.price === 'number' && i.price > 0)
          ? money(i.price).replace(/\.00(?=\D*$)/, '') + (i.size && i.size !== 'Default Title' ? ' · ' + esc(i.size) : '')
          : 'Request to order';
        return '<a class="fam" href="' + esc(i.url) + '" data-l10n-handle="' + esc(i.handle) + '" data-l10n-lang="' + esc(i.lang || '') + '">' +
          '<span class="fam-img">' + img + '</span>' +
          '<span class="fam-text"><span class="fam-title" data-l10n-name>' + esc(i.title) + '</span>' +
          '<span class="fam-price">' + sub + '</span></span></a>';
      }).join('');
      if (window.GloramousLocalise) window.GloramousLocalise(rvRow);
      rvHost.hidden = false;
    }

    var rvClear = rvHost.querySelector('[data-recent-clear]');
    if (rvClear) {
      rvClear.addEventListener('click', function () {
        rvWrite([]);
        rvHost.hidden = true;
        toast('Recently viewed cleared');
      });
    }
  }

  /* ═══ mobile menu: Esc and click-outside ════════════════════════════ */
  var navT = document.querySelector('.nav-toggle');
  if (navT) {
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && navT.open) {
        navT.open = false;
        navT.querySelector('summary').focus();
      }
    });
    document.addEventListener('click', function (e) {
      if (navT.open && !navT.contains(e.target)) navT.open = false;
    });
  }

  /* ═══ a small public surface, for assets/routine.js ═══════════════════
     The routine builder adds four or five products at once and then needs the
     header count and the drawer to agree with the server. Rather than have it
     reach into this file's internals, it gets three functions. */
  window.GloramousCart = {
    refresh: function () {
      return getCart().then(function (cart) {
        setCount(cart.item_count);
        render(cart);
        return cart;
      }).catch(function () {});
    },
    open: function (title) {
      return getCart().then(function (cart) {
        render(cart);
        setCount(cart.item_count);
        open(title);
        return cart;
      }).catch(function () {});
    },
    /* PREVIEW only: seed several lines at once, since there is no server to
       post them to. The real theme posts one /cart/add.js with an items array
       and then calls refresh(). */
    addMany: function (lines) {
      if (!PREVIEW) return;
      lines.forEach(function (l) {
        addToCart(l.id, 1, { title: l.title, variant: l.variant,
                             price: l.price, image: l.image });
      });
      return getCart().then(function (cart) {
        setCount(cart.item_count);
        render(cart);
        open('Added to cart');
        /* one product is named; "1 products added" read wrong in English and in Arabic */
        toast(lines.length === 1 ? lines[0].title + ' added to cart' : lines.length + ' products added');
        return cart;
      });
    }
  };

  /* ═══ product image zoom ════════════════════════════════════════════
     Removed 2026-09-24. theme.js opens the zoom (a native <dialog>). This older
     lightbox fired on the same click, so both opened at once: the screen
     flashed dark green under the white viewer, and the green one stayed
     stuck open after the white one closed. */
})();
